/**
 * Which of the three lists the trainer is looking at, and the figure beside each word.
 *
 * The canon draws `Wszystkie · Biblioteka · Twoje` as a radio row with a count pill on every
 * option, so the screen knows all three numbers whichever list it is drawing. They are counted over
 * the whole catalogue and never over the filtered view — the same rule the plan library's header
 * carries, and for the same reason: a count that moves when a filter moves is not a count of
 * anything the trainer asked about.
 *
 * ── The scope is the entity's ORIGIN facet under a different name ────────────────────────────
 * `entities/exercise-library` already owns "is this movement mine or the library's" — it is the
 * `origin` facet on `ExerciseQuery`, and `exerciseCatalogue` matches on it. This module does not
 * re-implement that; it names the three for the URL and for the switch, and hands the answer to the
 * query. A second predicate here would be a second copy of the rule.
 */

import type { ExerciseOrigin } from '~/entities/exercise-library';

/** The three words on the switch, in the canon's own order. */
export type ExerciseScope = 'all' | 'library' | 'own';

export const EXERCISE_SCOPES: readonly ExerciseScope[] = ['all', 'library', 'own'];

/** The scope, as the catalogue's own facet. The one place the two vocabularies meet. */
export function originOfScope(scope: ExerciseScope): ExerciseOrigin {
  switch (scope) {
    case 'library':
      return 'library';
    case 'own':
      return 'mine';
    case 'all':
      return 'all';
  }
}

/**
 * The facet, as one of the three words. The INVERSE of `originOfScope`, and it lives here for the
 * same reason that one does.
 *
 * It was a ternary inside the filter panel's `Źródło` radios, where nothing could test it and where
 * a fourth `ExerciseOrigin` would have compiled and quietly navigated the trainer to „Wszystkie".
 * Here the switch is exhaustive, so a fourth origin is a type error at the one place that has to
 * learn the word.
 */
export function scopeOfOrigin(origin: ExerciseOrigin): ExerciseScope {
  switch (origin) {
    case 'library':
      return 'library';
    case 'mine':
      return 'own';
    case 'all':
      return 'all';
  }
}

/** What the trainer HAS, per scope. Over the whole catalogue, never the filtered view. */
export interface ExerciseScopeCounts {
  readonly all: number;
  readonly library: number;
  readonly own: number;
}

/** A movement, as far as counting is concerned. */
interface Ownable {
  readonly isCustom: boolean;
}

/**
 * The three figures.
 *
 * `null` is not an option here and is not needed: the switch is only drawn once the catalogue has
 * answered, and a scope with no movements is a real zero rather than an unknown. What IS unknown
 * while the read is open is the header's three figures — those carry the em dash, and
 * `exerciseHeaderStats` is where that lives.
 */
export function exerciseScopeCounts(items: readonly Ownable[]): ExerciseScopeCounts {
  let own = 0;
  for (const item of items) if (item.isCustom) own += 1;
  return { all: items.length, library: items.length - own, own };
}
